/**
 * React Query Hooks for AI Service
 */

import { useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import type {
  ISegmentReplaceRequest,
  ISashOverlayRequest,
  ITaskSubmission,
  ITaskStatus,
} from "../types";
import {
  segmentReplaceApi,
  sashOverlayApi,
  getTaskStatusApi,
  downloadImageApi,
  debugFilesApi,
} from "../api";
import { AI_SERVICE_QUERY_KEY } from "../constants/queryKey";

/**
 * Hook to submit segment replace task
 * @returns Mutation hook for segment replace with abort controller
 */
export const useSegmentReplace = () => {
  const controllerRef = useRef<AbortController | null>(null);

  const mutation = useMutation<ITaskSubmission, Error, ISegmentReplaceRequest>({
    mutationFn: (data: ISegmentReplaceRequest) => {
      controllerRef.current = new AbortController();
      return segmentReplaceApi(data, controllerRef.current.signal);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to window ai blending task");
    },
  });

  return {
    ...mutation,
    controllerRef,
  };
};

/**
 * Hook to submit sash overlay task
 * @returns Mutation hook for sash overlay
 */
export const useSashOverlay = () => {
  return useMutation<ITaskSubmission, Error, ISashOverlayRequest>({
    mutationFn: (data: ISashOverlayRequest) => sashOverlayApi(data),
    onSuccess: (data) => {
      toast.success(`Task submitted successfully. Task ID: ${data.task_id}`);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to submit sash overlay task");
    },
  });
};

/**
 * Hook to get task status
 * @param taskId - Task ID to check status
 * @param options - Query options
 * @returns Query hook for task status
 */
export const useTaskStatus = (
  taskId: string,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
  }
) => {
  return useQuery<ITaskStatus, Error>({
    queryKey: [AI_SERVICE_QUERY_KEY.taskStatus, taskId],
    queryFn: () => getTaskStatusApi(taskId),
    enabled: options?.enabled !== false && !!taskId,
    refetchInterval: options?.refetchInterval,
  });
};

/**
 * Hook to poll task status until completion
 * Automatically polls every 2 seconds until task is complete or failed
 * @param taskId - Task ID to poll
 * @param options - Polling options
 * @returns Query hook with polling
 */
export const usePollTaskStatus = (
  taskId: string,
  options?: {
    enabled?: boolean;
    onSuccess?: (data: ITaskStatus) => void;
    onError?: (error: Error) => void;
  }
) => {
  const callbacksCalledRef = useRef({ success: false, error: false });

  const query = useQuery<ITaskStatus, Error>({
    queryKey: [AI_SERVICE_QUERY_KEY.taskStatus, taskId],
    queryFn: async () => {
      try {
        return await getTaskStatusApi(taskId);
      } catch (error) {
        throw error;
      }
    },
    enabled: options?.enabled !== false && !!taskId,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Stop polling if task is complete or failed
      if (data?.state === "SUCCESS" || data?.state === "FAILURE") {
        return false;
      }
      // Poll every second
      return 1000;
    },
    retry: false, // Don't retry on error - let error propagate
  });

  // Handle callbacks with useEffect to avoid calling multiple times
  useEffect(() => {
    if (!query.data) return;

    if (query.data.state === "SUCCESS" && !callbacksCalledRef.current.success) {
      callbacksCalledRef.current.success = true;
      if (options?.onSuccess) {
        options.onSuccess(query.data);
      }
    }

    if (query.data.state === "FAILURE" && !callbacksCalledRef.current.error) {
      callbacksCalledRef.current.error = true;
      const errorMessage = query.data.error || "Task failed";
      if (options?.onError) {
        options.onError(new Error(errorMessage));
      }
    }
  }, [query.data?.state, options]);

  // Reset callbacks when taskId changes
  useEffect(() => {
    callbacksCalledRef.current = { success: false, error: false };
  }, [taskId]);

  return query;
};

/**
 * Hook to download processed image
 * @returns Mutation hook for downloading image
 */
export const useDownloadImage = () => {
  return useMutation<Blob, Error, string>({
    mutationFn: (filename: string) => downloadImageApi(filename),
    onSuccess: (blob, filename) => {
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Image downloaded successfully");
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to download image");
    },
  });
};

/**
 * Hook to get debug files (development only)
 * @param enabled - Whether to enable the query
 * @returns Query hook for debug files
 */
export const useDebugFiles = (enabled: boolean = false) => {
  return useQuery({
    queryKey: [AI_SERVICE_QUERY_KEY.debugFiles],
    queryFn: () => debugFilesApi(),
    enabled,
  });
};

/**
 * Combined hook for complete workflow:
 * 1. Submit task
 * 2. Poll for status
 * 3. Download result when ready
 */
export const useAIProcessingWorkflow = () => {
  const { controllerRef: segmentReplaceController, ...segmentReplaceMutation } = useSegmentReplace();
  const sashOverlay = useSashOverlay();
  const downloadImage = useDownloadImage();

  return {
    segmentReplace: {
      ...segmentReplaceMutation,
      controllerRef: segmentReplaceController,
    },
    sashOverlay,
    downloadImage,

    /**
     * Process segment replace and download result
     */
    processSegmentReplace: async (data: ISegmentReplaceRequest) => {
      const result = await segmentReplaceMutation.mutateAsync(data);
      return result;
    },

    /**
     * Process sash overlay and download result
     */
    processSashOverlay: async (data: ISashOverlayRequest) => {
      const result = await sashOverlay.mutateAsync(data);
      return result;
    },
  };
};

// Re-export types for convenience
export type {
  ITaskStatus,
  ITaskSubmission,
  ISegmentReplaceRequest,
  ISashOverlayRequest,
} from "../types";
